/**
 * Health Advice Card Component
 * health_advice 테이블 또는 사용자 프로필 기반 건강 조언 카드
 * OpenAI로 요약된 1~2줄 조언 표시
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

interface HealthAdviceData {
  hasAdvice: boolean;
  advice_type: string;
  summary: string;
  original_advice: string;
  created_at?: string; // Optional - not displayed on dashboard
  style?: {
    icon: string;
    bgColor: string;
    borderColor: string;
    textColor: string;
  };
}

interface HealthAdviceCardProps {
  userId?: string;
  onCardLoaded?: (data: HealthAdviceData) => void;
}

/**
 * 건강 조언 카드 컴포넌트
 * - health_advice 테이블의 최신 데이터 조회
 * - OpenAI로 1~2줄 요약
 * - 데이터 없으면 프로필 기반 기본 조언 생성
 */
export const HealthAdviceCard: React.FC<HealthAdviceCardProps> = ({ userId, onCardLoaded }) => {
  const { user } = useAuth();
  const [adviceData, setAdviceData] = useState<HealthAdviceData | null>(null);
  const [loading, setLoading] = useState(true);

  // 사용자 이름 결정
  const userName = user?.name || '회원';

  useEffect(() => {
    const fetchHealthAdvice = async () => {
      try {
        setLoading(true);

        // localStorage에서 토큰 직접 조회
        const token = localStorage.getItem('authToken');
        
        if (!token) {
          console.warn('[HealthAdviceCard] No token found in localStorage');
          setLoading(false);
          return;
        }

        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        console.log('[HealthAdviceCard] Fetching from:', `${apiUrl}/api/health-advice/card`);
        console.log('[HealthAdviceCard] Token:', token.substring(0, 20) + '...');

        const response = await fetch(`${apiUrl}/api/health-advice/card`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        console.log('[HealthAdviceCard] Response status:', response.status);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(`Failed to fetch health advice: ${response.status} - ${errorData.error || response.statusText}`);
        }

        const result = await response.json();
        console.log('[HealthAdviceCard] Fetched data:', result);

        if (result.success && result.data) {
          setAdviceData(result.data);
          onCardLoaded?.(result.data);
        } else {
          // 데이터 로드 실패 시 기본 데이터 표시
          setAdviceData({
            hasAdvice: false,
            advice_type: 'general_health',
            summary: '건강 관리를 꾸준히 진행해주세요.',
            original_advice: '',
            // created_at 미포함 - 대시보드에 미표시
            style: {
              icon: '💚',
              bgColor: 'bg-emerald-50',
              borderColor: 'border-emerald-300',
              textColor: 'text-emerald-700',
            },
          });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '알 수 없는 오류';
        console.error('[HealthAdviceCard] Error fetching advice:', errorMessage);

        // 에러 발생 시에도 기본 데이터 표시
        setAdviceData({
          hasAdvice: false,
          advice_type: 'general_health',
          summary: '건강 관리를 꾸준히 진행해주세요.',
          original_advice: '',
          // created_at 미포함 - 대시보드에 미표시
          style: {
            icon: '💚',
            bgColor: 'bg-emerald-50',
            borderColor: 'border-emerald-300',
            textColor: 'text-emerald-700',
          },
        });
      } finally {
        setLoading(false);
      }
    };

    fetchHealthAdvice();
  }, [userId, onCardLoaded]);

  if (loading) {
    return (
      <div className="mx-4 mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 flex gap-3 shadow-sm animate-pulse">
        <div className="mt-0.5 text-2xl">⏳</div>
        <div className="flex-1">
          <div className="h-4 bg-slate-200 rounded w-1/2 mb-2"></div>
          <div className="h-3 bg-slate-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (!adviceData) {
    return null;
  }

  const { icon = '💚', bgColor = 'bg-emerald-50', borderColor = 'border-emerald-300', textColor = 'text-emerald-700' } = adviceData.style || {};

  return (
    <div className={`mx-4 mt-3 rounded-2xl border ${borderColor} ${bgColor} px-4 py-3 flex flex-col gap-2 shadow-sm mb-3`}>
      {/* 상단: 아이콘 + 내용 정보 */}
      <div className="flex gap-3">
        {/* 아이콘 */}
        <div className={`mt-0.5 text-2xl flex-shrink-0 ${textColor}`}>{icon}</div>

        {/* 내용 */}
        <div className="flex-1 min-w-0">
          {/* 헤더 - 사용자 이름 포함 */}
          <div className={`text-[15px] font-bold ${textColor} mb-1`}>
            {adviceData.hasAdvice 
              ? `📊 ${userName}님의 허비서AI 건강 조언` 
              : `💡 ${userName}님의 허비서AI 건강 관리 팁`}
          </div>

          {/* 요약된 조언 (1~2줄) */}
          <div className={`text-[13px] ${textColor.replace('text', 'text')} leading-relaxed`}>{adviceData.summary}</div>
        </div>

        {/* 더보기 버튼 (프로필 기반 생성일 때만) */}
        {!adviceData.hasAdvice && adviceData.original_advice && (
          <button
            type="button"
            className={`mt-0.5 text-[11px] font-semibold ${textColor} underline hover:no-underline cursor-pointer flex-shrink-0`}
            onClick={() => {
              if (adviceData.original_advice) {
                alert(`전체 조언:\n\n${adviceData.original_advice}`);
              }
            }}
          >
            상세보기
          </button>
        )}
      </div>
    </div>
  );
};

export default HealthAdviceCard;
